import { useState, useEffect } from 'react';

export type Row = Record<string, unknown>;

const TIMEOUT_MS = 30_000; // 30 s per attempt, two attempts max

// ── Module-level singleton ────────────────────────────────────────────────────
// The promise ALWAYS resolves (never rejects) so a background failure can't
// bubble up and break unrelated pages.  Errors are surfaced only when
// useReturnsData() is actively mounted (i.e. the user is on the analytics tab).

type FetchResult = { rows: Row[]; error: string | null };

let cachedResult: FetchResult | null = null;
let fetchPromise: Promise<FetchResult> | null = null;

// Single fetch attempt with a hard timeout via AbortController
async function doFetch(): Promise<Row[]> {
  const ctrl  = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    const res = await fetch('/api/portal-data?type=allReturns&jobIdx=0', {
      method: 'GET',
      credentials: 'include',
      signal: ctrl.signal,
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`Returns data load failed: ${res.status} — ${body.slice(0, 300)}`);
    }
    return await res.json() as Row[];
  } finally {
    clearTimeout(timer);
  }
}

// Two attempts (each with its own 30 s timeout), then resolves with an error
// result rather than rejecting — keeps unrelated pages safe.
async function fetchWithRetry(): Promise<FetchResult> {
  try {
    return { rows: await doFetch(), error: null };
  } catch {
    try {
      return { rows: await doFetch(), error: null };
    } catch (e2) {
      return { rows: [], error: e2 instanceof Error ? e2.message : String(e2) };
    }
  }
}

export function preloadReturnsData(): void {
  if (cachedResult || fetchPromise) return; // already done or in-flight
  fetchPromise = fetchWithRetry().then(result => {
    cachedResult = result;
    // On error clear the promise so the next mount can trigger a fresh attempt
    if (result.error) fetchPromise = null;
    return result;
  });
}

export function invalidateReturnsCache(): void {
  cachedResult  = null;
  fetchPromise  = null;
}

export function useReturnsData() {
  const [rows,    setRows]    = useState<Row[]>(cachedResult?.rows  ?? []);
  const [loading, setLoading] = useState<boolean>(!cachedResult);
  const [error,   setError]   = useState<string | null>(cachedResult?.error ?? null);

  useEffect(() => {
    // Happy path — data already cached successfully
    if (cachedResult && !cachedResult.error) {
      setRows(cachedResult.rows);
      setLoading(false);
      setError(null);
      return;
    }

    // Previous attempt errored — auto-retry now that the user is on this tab
    if (cachedResult?.error) {
      cachedResult = null;
      setError(null);
      setLoading(true);
    }

    // Start (or join an in-flight) fetch
    preloadReturnsData();

    if (!fetchPromise) {
      setLoading(false);
      return;
    }

    fetchPromise.then(result => {
      setRows(result.rows);
      setError(result.error);
      setLoading(false);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function reload() {
    invalidateReturnsCache();
    setLoading(true);
    setError(null);
    setRows([]);
    preloadReturnsData();
    fetchPromise?.then(result => {
      setRows(result.rows);
      setError(result.error);
      setLoading(false);
    });
  }

  return { rows, loading, error, reload };
}
